import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { run_agent_loop } from "@tellescope/ai-agent";
import type { AIMessage } from "@tellescope/ai-agent";
import { createAnalyticsAgent } from "@tellescope/ai-agent-tellescope";
import { useAuth, useTellescopeMutation } from "@tellescope/tanstack-query";
import type { AIChatMessageWithVisualization, AskAIState } from "./types";
import { generateExplanation } from "./utils/generateExplanation";
import { inferVisualizationType } from "./utils/inferVisualizationType";

interface AnalyticsOutput {
  modelName?: string;
  aggregation?: unknown[];
  results?: Array<Record<string, unknown>>;
}

export const useAskAIPageLogic = () => {
  const { t } = useTranslation("ai");
  const { session, currentUser } = useAuth();

  const [state, setState] = useState<AskAIState>({
    messages: [],
    isLoading: false,
    error: null,
    orchestrationId: null,
    conversationId: null,
  });

  const [inputValue, setInputValue] = useState("");
  const lastUserQueryRef = useRef<string>("");
  const conversationIdRef = useRef<string | null>(null);

  // Create agent lazily (memoized based on session)
  const agent = useMemo(() => {
    if (!session) return null;
    return createAnalyticsAgent(session);
  }, [session]);

  // Mutations for ai_conversations with cache invalidation
  const createConversationMutation = useTellescopeMutation({
    model: "ai_conversations",
    method: "createOne",
  });

  const updateConversationMutation = useTellescopeMutation({
    model: "ai_conversations",
    method: "updateOne",
  });

  // Generate or get orchestrationId for this conversation
  const orchestrationId = useMemo(() => {
    if (state.orchestrationId) return state.orchestrationId;
    return crypto.randomUUID();
  }, [state.orchestrationId]);

  const sendMessage = useCallback(async () => {
    if (!agent || !inputValue.trim() || !session) return;

    const userMessageText = inputValue.trim();
    lastUserQueryRef.current = userMessageText;
    setInputValue("");

    // Create user message
    const userMessage: AIChatMessageWithVisualization = {
      id: crypto.randomUUID(),
      role: "user",
      text: userMessageText,
      timestamp: new Date(),
    };

    // Update state with user message and set loading
    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
      error: null,
      orchestrationId: orchestrationId,
    }));

    // Prepare messages for agent (convert to AIMessage format)
    const messagesForAgent: AIMessage[] = [
      ...state.messages.map((m) => ({
        role: m.role,
        text: m.text,
        timestamp: m.timestamp,
      })),
      {
        role: userMessage.role,
        text: userMessage.text,
        timestamp: userMessage.timestamp,
      },
    ];

    try {
      // Create root conversation thread using mutation for cache invalidation
      const rootConversation = await createConversationMutation.mutateAsync({
        modelName: "endusers",
        orchestrationId,
        type: "analytics-chat",
        messages: [
          {
            role: "user" as const,
            text: JSON.stringify({ type: "text", content: userMessageText }),
            timestamp: new Date(),
            tokens: 0,
          },
        ],
      });
      const conversationId = rootConversation.id;
      conversationIdRef.current = conversationId;
      setState((prev) => ({ ...prev, conversationId }));

      await run_agent_loop(agent, messagesForAgent, {
        orchestrationId,
        onChange: async (updatedAgent, transitionType) => {
          if (transitionType === "Success") {
            // Get the latest result
            const results = updatedAgent.results || [];
            if (results.length > 0) {
              const latestResult = results[results.length - 1];

              // Infer visualization type from results
              const output = latestResult?.output as AnalyticsOutput | undefined;
              const analyticsResults = output?.results;
              const inference = inferVisualizationType(analyticsResults);

              // Append analytics-result to root thread using mutation for cache invalidation
              const currentConversationId = conversationIdRef.current;
              if (currentConversationId && analyticsResults && analyticsResults.length > 0) {
                await updateConversationMutation.mutateAsync({
                  id: currentConversationId,
                  messages: [
                    {
                      role: "assistant" as const,
                      text: JSON.stringify({
                        type: "analytics-result",
                        visualizationType: inference.type,
                        data: analyticsResults,
                        inference,
                      }),
                      timestamp: new Date(),
                      tokens: 0,
                    },
                  ],
                  _options: { replaceObjectFields: false },
                });
              }

              // Generate appropriate text based on visualization type
              let messageText = "";
              if (inference.type === "single-stat") {
                // For single-stat, show only the visualization (no text needed)
                messageText = "";
              } else if (analyticsResults && analyticsResults.length > 0 && session) {
                // For complex visualizations, generate an LLM explanation
                messageText = await generateExplanation(
                  session,
                  analyticsResults,
                  lastUserQueryRef.current,
                  inference.type,
                  orchestrationId
                );
              }

              // Append text explanation to root thread (if generated) using mutation for cache invalidation
              if (currentConversationId && messageText) {
                await updateConversationMutation.mutateAsync({
                  id: currentConversationId,
                  messages: [
                    {
                      role: "assistant" as const,
                      text: JSON.stringify({ type: "text", content: messageText }),
                      timestamp: new Date(),
                      tokens: 0,
                    },
                  ],
                  _options: { replaceObjectFields: false },
                });
              }

              // Create assistant message with visualization
              const assistantMessage: AIChatMessageWithVisualization = {
                id: crypto.randomUUID(),
                role: "assistant",
                text: messageText,
                timestamp: new Date(),
                visualization:
                  analyticsResults && analyticsResults.length > 0
                    ? {
                        inference,
                        data: analyticsResults,
                      }
                    : undefined,
              };

              setState((prev) => ({
                ...prev,
                messages: [...prev.messages, assistantMessage],
                isLoading: false,
              }));
            }
          }
        },
        onError: async (_, error) => {
          const errorText = t("Sorry, I encountered an error: {{message}}", { message: error.message });
          const errorMessage: AIChatMessageWithVisualization = {
            id: crypto.randomUUID(),
            role: "assistant",
            text: errorText,
            timestamp: new Date(),
          };

          // Persist error to conversation
          const currentConversationId = conversationIdRef.current;
          if (currentConversationId) {
            await updateConversationMutation.mutateAsync({
              id: currentConversationId,
              messages: [
                {
                  role: "assistant" as const,
                  text: JSON.stringify({ type: "error", content: errorText }),
                  timestamp: new Date(),
                  tokens: 0,
                },
              ],
              _options: { replaceObjectFields: false },
            });
          }

          setState((prev) => ({
            ...prev,
            messages: [...prev.messages, errorMessage],
            isLoading: false,
            error: error.message,
          }));
        },
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t("An unexpected error occurred");

      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, [
    agent,
    inputValue,
    session,
    state.messages,
    orchestrationId,
    t,
    createConversationMutation,
    updateConversationMutation,
  ]);

  const clearConversation = useCallback(() => {
    conversationIdRef.current = null;
    setState({
      messages: [],
      isLoading: false,
      error: null,
      orchestrationId: null,
      conversationId: null,
    });
  }, []);

  // Check if we have received a response (assistant message exists)
  const hasResponse = state.messages.some((m) => m.role === "assistant");

  return {
    messages: state.messages,
    isLoading: state.isLoading,
    error: state.error,
    conversationId: state.conversationId,
    inputValue,
    setInputValue,
    sendMessage,
    clearConversation,
    isReady: !!agent && !!session,
    currentUser,
    hasResponse,
  };
};
